import React, { useEffect } from "react";

import Input from "../../controls/Input";
import AddBomToIngredientPopup from "./modals/AddBomToIngredientPopup";
import RecipeBomItemsTable from "./RecipeBomItemsTable";
import RecipeLineItemsTable from "./RecipeLineItemsTable";
import type {
  AddIngredientPayload,
  BomItem,
  OverlayState,
  PaginatedBomItems,
  RecipeFormValues,
  RecipeLineItem,
} from "../../../features/recipes/recipeTypes";
import { emptyRecipeForm } from "../../../features/recipes/recipeTypes";
import { updateObjectInArray } from "../../../shared/utility";
import { useForm } from "../useForm";

const fieldClassName =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-35-custom shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm";

function digitsAndDecimal(value: string) {
  const cleaned = value.replace(/[^\d.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex === -1) return cleaned;
  return (
    cleaned.slice(0, dotIndex + 1) +
    cleaned.slice(dotIndex + 1).replace(/\./g, "")
  );
}

interface RecipesFormProps {
  addBomToIngredient: (
    payload: AddIngredientPayload,
  ) => Promise<{ success?: boolean; data?: RecipeLineItem } | undefined>;
  askDeleteRecipe: (item: RecipeLineItem, type: number) => void;
  bomItems: PaginatedBomItems;
  bomItemsLoading: boolean;
  getBomItems: (search: string, page: number) => void;
  onCancel: () => void;
  packagingItems: RecipeLineItem[];
  recipe: RecipeFormValues;
  recipeItems: RecipeLineItem[];
  saveRecipe: (values: RecipeFormValues, isEdit: boolean) => void;
  setEmptyModelOpen: (state: OverlayState) => void;
  setPackagingItems: (items: RecipeLineItem[]) => void;
  setRecipeItems: (items: RecipeLineItem[]) => void;
}

export default function RecipesForm({
  addBomToIngredient,
  askDeleteRecipe,
  bomItems,
  bomItemsLoading,
  getBomItems,
  onCancel,
  packagingItems,
  recipe,
  recipeItems,
  saveRecipe,
  setEmptyModelOpen,
  setPackagingItems,
  setRecipeItems,
}: RecipesFormProps) {
  const { values, setValues, errors, handleInputChange } = useForm(
    emptyRecipeForm,
    false,
  );
  const formValues = values as RecipeFormValues;
  const formErrors = errors as Record<string, string | null>;

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    saveRecipe(formValues, Number(formValues.id) > 0);
  };

  const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange({
      target: {
        name: event.target.name,
        value: digitsAndDecimal(event.target.value),
      },
    });
  };

  useEffect(() => {
    if (Number(recipe.id) > 0) {
      setValues(recipe);
    }
  }, [recipe, setValues]);

  const calculateLossesFunc = (
    newBatchSize: string | number,
    newRecipeItems: RecipeLineItem[],
    newPackagingItems: RecipeLineItem[],
  ) => {
    const batchSize = parseFloat(String(newBatchSize));
    const manPercent = parseFloat(String(formValues.manufacture_loss_percent));
    const manLossKg = batchSize * (manPercent / 100);
    const manufactureLossCalculated = parseFloat(
      String(batchSize - manLossKg),
    ).toFixed(2);
    const fermentLossKg =
      parseFloat(manufactureLossCalculated) *
      (parseFloat(String(formValues.fermentation_loss_percent)) / 100);
    const fermentationLossCalculated = parseFloat(
      parseFloat(
        String(parseFloat(manufactureLossCalculated) - fermentLossKg),
      ).toFixed(2),
    );
    const cookingLossKg = parseFloat(
      String(
        fermentationLossCalculated *
          (parseFloat(String(formValues.cooking_loss_percent)) / 100),
      ),
    );
    const cookingLossCalculated = parseFloat(
      parseFloat(String(fermentationLossCalculated - cookingLossKg)).toFixed(2),
    );
    const slicingLossKg = parseFloat(
      String(
        cookingLossCalculated *
          (parseFloat(String(formValues.slicing_loss_percent)) / 100),
      ),
    );
    const slicingLossCalculated = parseFloat(
      parseFloat(String(cookingLossCalculated - slicingLossKg)).toFixed(2),
    );

    setValues({
      ...formValues,
      batch_size: newBatchSize,
      manufacture_loss_calculated: manufactureLossCalculated,
      fermentation_loss_calculated: fermentationLossCalculated,
      cooking_loss_calculated: cookingLossCalculated,
      slicing_loss_calculated: slicingLossCalculated,
      processed: slicingLossCalculated,
      target_production_carpan: (batchSize / slicingLossCalculated).toFixed(6),
    });

    newRecipeItems.forEach((item) => {
      item.recipe_per_kg = (
        parseFloat(String(item.quantity)) / slicingLossCalculated
      ).toFixed(6);
    });
    setRecipeItems(newRecipeItems);

    newPackagingItems.forEach((item) => {
      item.recipe_per_kg = (
        parseFloat(String(item.quantity)) / slicingLossCalculated
      ).toFixed(6);
    });
    setPackagingItems(newPackagingItems);
  };

  const waitAddBomToIngredient = async (confirmValue: Record<string, any>) => {
    const payload: AddIngredientPayload = {
      recipe_id: Number(formValues.id),
      bom_id: confirmValue.id,
      bom_name: confirmValue.name,
      bom_plu: confirmValue.plu,
      description: confirmValue.description,
      unit_of_measure: confirmValue.unit_of_measure,
      product_types_value: confirmValue.product_types_value,
      product_types_label: confirmValue.product_types_label,
      product_type: confirmValue.product_type,
      quantity: confirmValue.quantity,
    };

    const callResult = await addBomToIngredient(payload);
    if (!callResult?.success || !callResult.data) return;

    let nextPackagingItems = [...packagingItems];
    let nextRecipeItems = [...recipeItems];
    let nextBatchSize = formValues.batch_size;

    if (Number(callResult.data.is_packaging) === 1) {
      nextPackagingItems = [...packagingItems, callResult.data];
      setPackagingItems(nextPackagingItems);
    } else {
      nextRecipeItems = [...recipeItems, callResult.data];
      nextBatchSize =
        parseFloat(String(nextBatchSize)) +
        parseFloat(String(payload.quantity));
      setRecipeItems(nextRecipeItems);
      setValues({
        ...formValues,
        batch_size: nextBatchSize,
      });
    }

    setTimeout(() => {
      calculateLossesFunc(nextBatchSize, nextRecipeItems, nextPackagingItems);
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
    }, 500);
  };

  const onAddBomToIngredient = (newBomItem: BomItem) => {
    setEmptyModelOpen({
      childComponent: (
        <AddBomToIngredientPopup
          bomItem={newBomItem}
          confirmAction={(confirmValue) => {
            void waitAddBomToIngredient(confirmValue);
          }}
          isEdit={false}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const updateIngredientQuantity = (
    ingredient: Record<string, any>,
    index: number,
    oldQuantity: string | number,
  ) => {
    let nextPackagingItems = [...packagingItems];
    let nextRecipeItems = [...recipeItems];
    let nextBatchSize = formValues.batch_size;

    if (Number(ingredient.is_packaging) === 1) {
      const ingredientBeingEdited = {
        ...packagingItems[index],
        ...ingredient,
      };
      nextPackagingItems = updateObjectInArray(packagingItems, {
        index,
        item: ingredientBeingEdited,
      });
      setPackagingItems(nextPackagingItems);
    } else {
      const ingredientBeingEdited = {
        ...recipeItems[index],
        ...ingredient,
      };
      nextRecipeItems = updateObjectInArray(recipeItems, {
        index,
        item: ingredientBeingEdited,
      });
      setRecipeItems(nextRecipeItems);
      nextBatchSize =
        parseFloat(String(nextBatchSize)) -
        parseFloat(String(oldQuantity)) +
        parseFloat(String(ingredient.quantity));
      setValues({
        ...formValues,
        batch_size: nextBatchSize,
      });
    }

    setTimeout(() => {
      calculateLossesFunc(nextBatchSize, nextRecipeItems, nextPackagingItems);
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
    }, 500);
  };

  const openUpdateIngredientQuantity = (
    ingredient: RecipeLineItem,
    index: number,
  ) => {
    setEmptyModelOpen({
      childComponent: (
        <AddBomToIngredientPopup
          bomItem={{ ...ingredient, name: ingredient.bom_name }}
          index={index}
          confirmAction={(confirmValue, lineIndex) => {
            updateIngredientQuantity(
              confirmValue,
              lineIndex ?? index,
              ingredient.quantity,
            );
          }}
          isEdit={true}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-3 pt-2 lg:flex-row">
          <div className="mt-2 min-w-0 w-full space-y-2 lg:w-1/2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                label="Recipe #"
                name="recipe_number"
                onChange={handleInputChange}
                value={formValues.recipe_number}
                labelOn={true}
                type="text"
                className={fieldClassName}
                error={formErrors.recipe_number}
              />
              <Input
                label="Recipe Name"
                name="recipe_name"
                onChange={handleInputChange}
                value={formValues.recipe_name}
                labelOn={true}
                type="text"
                className={fieldClassName}
                error={formErrors.recipe_name}
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <Input
                label="Batch size"
                name="batch_size"
                onChange={handleNumericChange}
                value={formValues.batch_size}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.batch_size}
              />
              <Input
                label="Weight/unit"
                name="weight_per_unit"
                onChange={handleNumericChange}
                value={formValues.weight_per_unit}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.weight_per_unit}
              />
              <Input
                label="Unit/case"
                name="unit_per_case"
                onChange={handleNumericChange}
                value={formValues.unit_per_case}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.unit_per_case}
              />
              <Input
                label="Weight/case"
                name="weight_per_case"
                onChange={handleNumericChange}
                value={formValues.weight_per_case}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.weight_per_case}
              />
              <Input
                label="Labour (%)"
                name="labour"
                onChange={handleNumericChange}
                value={formValues.labour}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.labour}
              />
              <Input
                label="Overhead (%)"
                name="overhead"
                onChange={handleNumericChange}
                value={formValues.overhead}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.overhead}
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <Input
                label="Manufacture loss (%)"
                name="manufacture_loss_percent"
                onChange={handleNumericChange}
                value={formValues.manufacture_loss_percent}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.manufacture_loss_percent}
              />
              <Input
                label="Fermentation loss (%)"
                name="fermentation_loss_percent"
                onChange={handleNumericChange}
                value={formValues.fermentation_loss_percent}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.fermentation_loss_percent}
              />
              <Input
                label="Cooking loss (%)"
                name="cooking_loss_percent"
                onChange={handleNumericChange}
                value={formValues.cooking_loss_percent}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.cooking_loss_percent}
              />
              <Input
                label="Slicing loss (%)"
                name="slicing_loss_percent"
                onChange={handleNumericChange}
                value={formValues.slicing_loss_percent}
                labelOn={true}
                type="text"
                inputMode="decimal"
                className={fieldClassName}
                error={formErrors.slicing_loss_percent}
              />
              <Input
                label="Processed"
                name="processed"
                onChange={handleInputChange}
                value={formValues.processed}
                labelOn={true}
                disabled={true}
                type="text"
                className={fieldClassName}
                error={formErrors.processed}
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <Input
                label="Manufacture loss"
                name="manufacture_loss_calculated"
                onChange={handleInputChange}
                value={formValues.manufacture_loss_calculated}
                labelOn={true}
                disabled={true}
                type="text"
                className={fieldClassName}
                error={formErrors.manufacture_loss_calculated}
              />
              <Input
                label="Fermentation loss"
                name="fermentation_loss_calculated"
                onChange={handleInputChange}
                value={formValues.fermentation_loss_calculated}
                labelOn={true}
                disabled={true}
                type="text"
                className={fieldClassName}
                error={formErrors.fermentation_loss_calculated}
              />
              <Input
                label="Cooking loss"
                name="cooking_loss_calculated"
                onChange={handleInputChange}
                value={formValues.cooking_loss_calculated}
                labelOn={true}
                disabled={true}
                type="text"
                className={fieldClassName}
                error={formErrors.cooking_loss_calculated}
              />
              <Input
                label="Slicing loss"
                name="slicing_loss_calculated"
                onChange={handleInputChange}
                value={formValues.slicing_loss_calculated}
                labelOn={true}
                disabled={true}
                type="text"
                className={fieldClassName}
                error={formErrors.slicing_loss_calculated}
              />
              <Input
                label="Target production loss"
                name="target_production_carpan"
                onChange={handleInputChange}
                value={formValues.target_production_carpan}
                labelOn={true}
                disabled={true}
                type="text"
                className={fieldClassName}
                error={formErrors.target_production_carpan}
              />
              <div className="flex items-end">
                <button
                  type="button"
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                  onClick={() =>
                    calculateLossesFunc(
                      formValues.batch_size,
                      recipeItems,
                      packagingItems,
                    )
                  }
                >
                  Calculate
                </button>
              </div>
            </div>
            <RecipeLineItemsTable
              title="Recipe ingredients"
              items={recipeItems}
              deleteType={0}
              openUpdateIngredientQuantity={openUpdateIngredientQuantity}
              askDeleteRecipe={askDeleteRecipe}
            />
            <RecipeLineItemsTable
              title="Packaging items"
              items={packagingItems}
              deleteType={1}
              openUpdateIngredientQuantity={openUpdateIngredientQuantity}
              askDeleteRecipe={askDeleteRecipe}
            />
          </div>
          <div className="min-w-0 w-full lg:w-1/2">
            <RecipeBomItemsTable
              getBomItems={getBomItems}
              bomItems={bomItems}
              bomItemsLoading={bomItemsLoading}
              onAddBomToIngredient={onAddBomToIngredient}
              recipe={recipe}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-wrap justify-end gap-3 px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={handleSubmit}
        >
          {Number(formValues.id) > 0 ? "Save" : "Create"}
        </button>
      </div>
    </div>
  );
}
